/**
 * Signup wizard route components. Each one is a thin wrapper that reads wizard
 * state from SignupFlowContext and routes the user to the next step on success.
 */
import { lazy, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/AuthContext';
import { useSignupFlow } from '@contexts/SignupFlowContext';
import { authNafathInitiate, authNafathStatus } from '@services/api';
import { queryKeys } from '../../queryKeys';
import { saveSignupOtpSession } from '@utils/signupOtpSession';
import { navigateAfterAuth } from '@utils/returnAfterLogin';

const RegisterPage = lazy(() => import('@pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const SignUpOtpPage = lazy(() => import('@pages/SignUpOtpPage').then((m) => ({ default: m.SignUpOtpPage })));
const SignUpCompletePage = lazy(() =>
  import('@pages/SignUpCompletePage').then((m) => ({ default: m.SignUpCompletePage }))
);
const AuthNafathPage = lazy(() => import('@pages/AuthNafathPage').then((m) => ({ default: m.AuthNafathPage })));
const AuthAddressPage = lazy(() => import('@pages/AuthAddressPage').then((m) => ({ default: m.AuthAddressPage })));
const AuthSuccessPage = lazy(() => import('@pages/AuthSuccessPage').then((m) => ({ default: m.AuthSuccessPage })));

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_TRIES = 60; // 120 s total

const COOLDOWN_SECS = (() => {
  const prod = Number(import.meta.env?.VITE_NAFATH_RETRY_COOLDOWN_SECS) || 60;
  const debug = Number(import.meta.env?.VITE_NAFATH_RETRY_COOLDOWN_DEBUG_SECS) || 10;
  return import.meta.env?.DEV ? debug : prod;
})();

function useLang() {
  const { lang } = useParams<{ lang: string }>();
  return lang === 'ar' ? 'ar' : 'en';
}

export function RegisterRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { setSignUpPhone } = useSignupFlow();

  return (
    <RegisterPage
      contentOnly
      showRightPanel={false}
      onNext={(phone) => {
        saveSignupOtpSession({ phone, authContext: 'broker' });
        setSignUpPhone(phone);
        navigate(`/${l}/signup-verify`);
      }}
    />
  );
}

export function SignUpOtpRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { signUpPhone } = useSignupFlow();

  const handleSessionExpired = () => {
    navigate(`/${l}/signup?reason=session_expired`, { replace: true });
  };

  return (
    <SignUpOtpPage
      contentOnly
      showRightPanel={false}
      onSessionExpired={handleSessionExpired}
      onBack={() => navigate(`/${l}/signup`)}
      onVerify={(authData) => {
        const u = authData.user;
        if (!authData.profileCompleted) {
          navigate(`/${l}/signup-complete`, { replace: true });
          return;
        }
        if (!u.nafathVerified) {
          navigate(`/${l}/nafath-verification`, { replace: true });
          return;
        }
        navigateAfterAuth(navigate, `/${l}/main-brokerage`);
      }}
      phoneNumber={signUpPhone || undefined}
    />
  );
}

export function SignUpCompleteRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { clearSession } = useAuth();

  return (
    <SignUpCompletePage
      contentOnly
      showRightPanel={false}
      onBack={() => {
        import('@services/api').then(({ authLogout }) => authLogout().catch(() => {})).finally(() => {
          clearSession();
          navigate(`/${l}/signup`, { replace: true });
        });
      }}
      onContinue={() => navigate(`/${l}/nafath-verification`)}
    />
  );
}

export function AuthNafathRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setNafathResult } = useSignupFlow();
  const { isAuthenticated, profileCompleted, refreshAuth } = useAuth();
  const showBack = !(isAuthenticated && profileCompleted === true);

  const [pollingTransId, setPollingTransId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // useQuery deduplicates concurrent subscribers — StrictMode's double-mount both see the same
  // in-flight request, so only one POST /nafath/initiate is made.
  // gcTime:0 clears the cache entry when the component unmounts (navigation away), ensuring a
  // fresh initiation on each visit without reusing a stale transId.
  const { data: nafathSession, isFetching, isError: initFailed } = useQuery({
    queryKey: queryKeys.nafathInitiate(refetchKey),
    queryFn: authNafathInitiate,
    staleTime: Infinity,
    retry: false,
    gcTime: 0,
  });

  const transId = nafathSession?.transId ?? null;
  const randomCode = nafathSession?.random;

  // Start polling when a new transId arrives from the query.
  useEffect(() => {
    if (transId && !isFetching && transId !== pollingTransId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(null);
      setPollingTransId(transId);
    }
  }, [transId, pollingTransId, isFetching]);

  // Start retry cooldown whenever a fresh transId is issued.
  useEffect(() => {
    if (transId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCooldownRemaining(COOLDOWN_SECS);
    }
  }, [transId]);

  // Tick cooldown down every second.
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCooldownRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  useEffect(() => {
    if (initFailed) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(t('auth.nafath.error'));
    }
  }, [initFailed, t]);

  useEffect(() => {
    if (!pollingTransId) return;
    let cancelled = false;
    let tries = 0;

    const interval = setInterval(async () => {
      tries += 1;
      if (cancelled) return;
      if (tries > POLL_MAX_TRIES) {
        clearInterval(interval);
        setPollingTransId(null);
        setError(t('auth.nafath.timeout'));
        return;
      }
      try {
        const status = await authNafathStatus(pollingTransId);
        if (status.status === 'COMPLETED') {
          clearInterval(interval);
          if (!cancelled) {
            setPollingTransId(null);
            setNafathResult({
              nationalAddresses: status.nationalAddresses,
              fullName: status.fullName,
              fullNameAr: status.fullNameAr,
              name: status.name,
              email: status.email,
            });
            await refreshAuth();
            navigate(`/${l}/select-national-address`);
          }
        } else if (status.status === 'REJECTED' || status.status === 'EXPIRED') {
          clearInterval(interval);
          if (!cancelled) {
            setPollingTransId(null);
            setError(t('auth.nafath.rejected'));
          }
        }
      } catch {
        // transient poll error — keep trying
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pollingTransId, l, navigate, setNafathResult, t, refreshAuth]);

  const handleResend = useCallback(() => {
    if (cooldownRemaining > 0) return;
    setPollingTransId(null);
    setError(null);
    setRefetchKey((k) => k + 1);
  }, [cooldownRemaining]);

  return (
    <AuthNafathPage
      contentOnly
      showRightPanel={false}
      onBack={showBack ? () => navigate(`/${l}/signup-complete`) : undefined}
      verificationCode={randomCode}
      onResendCode={handleResend}
      polling={!!pollingTransId || isFetching}
      cooldownRemaining={cooldownRemaining}
      error={error}
    />
  );
}

export function AuthAddressRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { nafathResult } = useSignupFlow();

  return (
    <AuthAddressPage
      contentOnly
      showRightPanel={false}
      onBack={() => navigate(`/${l}/nafath-verification`)}
      onNext={() => navigate(`/${l}/success`)}
      addressesFromNafath={nafathResult?.nationalAddresses}
    />
  );
}

export function AuthSuccessRoute() {
  const l = useLang();
  const navigate = useNavigate();

  return (
    <AuthSuccessPage
      contentOnly
      showRightPanel={false}
      onStartNow={() => navigateAfterAuth(navigate, `/${l}/main-brokerage`)}
    />
  );
}
